import { useState } from 'react';
import { Avatar, Box, ButtonBase, Divider, Menu, MenuItem, Typography } from '@mui/material';
import NotificationIcon from 'src/icons/NotificationIcon';
import ChevronUp from 'src/icons/ChevroneUp';
import HelpIcon from 'src/icons/QustionXs';
import Lock from 'src/icons/Lock';
import Exit from 'src/icons/Exit';
import Mail from 'src/icons/Mail';
import { UserIcon } from 'src/icons/UserIcon';
import { history } from 'src/utils/history';
import EventService from 'src/services/EventService';
import { ROUTES_URL } from 'src/constants/routes';
import sessionStore from 'src/stores/session';
import userStore from 'src/stores/user';
import restorePasswordStore from 'src/stores/restorePassword';
import AppearancePicker from 'src/components/AppearancePicker';
import { SUPPORT_URL } from 'src/constants/support';
import { IconAdjustmentsHorizontal } from '@tabler/icons-react';

export default function ProfileDropdown({
    email,
    bonusPoints,
    collapsed = false,
}: {
    email: string;
    bonusPoints: number;
    collapsed?: boolean;
}) {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const close = () => setAnchor(null);
    const go = (path: string) => {
        close();
        history.push(path);
        EventService.dispatch('CLOSE_MENU');
    };
    const menuSx = {
        gap: 1.5,
        py: 1.2,
        px: 1.5,
        fontSize: 14,
        borderRadius: 1.5,
        justifyContent: 'start',
        '& svg': { width: 20, height: 20, flexShrink: 0 },
    };
    return (
        <>
            <ButtonBase
                aria-label="Меню профиля"
                aria-haspopup="menu"
                aria-controls={anchor ? 'profile-menu' : undefined}
                aria-expanded={!!anchor}
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{
                    width: '100%',
                    p: collapsed ? 0.5 : 1.2,
                    gap: 1.2,
                    justifyContent: collapsed ? 'center' : 'start',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    textAlign: 'left',
                }}
            >
                <Avatar
                    sx={{
                        width: 36,
                        height: 36,
                        fontSize: 14,
                        bgcolor: '#1f1f1f',
                        color: 'var(--lm-accent-contrast)',
                    }}
                >
                    {(email || 'Л').slice(0, 1).toUpperCase()}
                </Avatar>
                {!collapsed && (
                    <>
                        <Box
                            flex={1}
                            minWidth={0}
                        >
                            <Typography
                                fontSize={13}
                                fontWeight={500}
                                noWrap
                            >
                                {email || 'Профиль'}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {bonusPoints > 0 ? `${bonusPoints} баллов` : 'Настройки аккаунта'}
                            </Typography>
                        </Box>
                        <ChevronUp />
                    </>
                )}
            </ButtonBase>
            <Menu
                id="profile-menu"
                anchorEl={anchor}
                open={!!anchor}
                onClose={close}
                anchorOrigin={{ vertical: 'top', horizontal: collapsed ? 'right' : 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        width: 278,
                        maxWidth: 'calc(100vw - 24px)',
                        bgcolor: 'background.default',
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 0.5,
                        borderRadius: 3,
                        maxHeight: 'calc(100dvh - 90px)',
                    },
                }}
            >
                <MenuItem
                    sx={menuSx}
                    onClick={() => go(ROUTES_URL.PROFILE)}
                >
                    <UserIcon />
                    Профиль
                </MenuItem>
                <MenuItem
                    sx={menuSx}
                    onClick={() => go(ROUTES_URL.NOTIFICATIONS)}
                >
                    <NotificationIcon />
                    Уведомления
                </MenuItem>
                <MenuItem
                    component="a"
                    sx={menuSx}
                    href={SUPPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={close}
                >
                    <HelpIcon />
                    Помощь
                </MenuItem>
                <MenuItem
                    sx={menuSx}
                    onClick={() => {
                        close();
                        window.dispatchEvent(new Event('lm-open-analytics-settings'));
                        EventService.dispatch('CLOSE_MENU');
                    }}
                >
                    <IconAdjustmentsHorizontal />
                    Настройки аналитики
                </MenuItem>
                <Divider sx={{ my: 1, mx: 1.5 }} />
                <Box
                    onKeyDown={(e) => {
                        if (e.key !== 'Escape') e.stopPropagation();
                    }}
                >
                    <AppearancePicker />
                </Box>
                <Divider sx={{ my: 1, mx: 1.5 }} />
                <MenuItem
                    sx={menuSx}
                    onClick={() => {
                        restorePasswordStore.getState().setEmail(userStore.getState().email);
                        go(ROUTES_URL.RESTORE_PASSWORD);
                    }}
                >
                    <Lock />
                    Изменить пароль
                </MenuItem>
                <MenuItem
                    sx={menuSx}
                    onClick={() => go(ROUTES_URL.CHANGE_EMAIL)}
                >
                    <Mail />
                    Изменить почту
                </MenuItem>
                <MenuItem
                    sx={menuSx}
                    onClick={() => {
                        go(ROUTES_URL.LOGIN);
                        sessionStore.getState().logout();
                    }}
                >
                    <Exit />
                    Выйти
                </MenuItem>
            </Menu>
        </>
    );
}
